import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { endpoints } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QUERY_KEY_QUEUES_UNPAGINATED } from '../../../business/queues/useUnpaginatedQueues';
import AnnotationsActionModal, {
  AnnotationsActionModalProps,
} from '../../../components/AnnotationsActionModal';
import { notify } from '../../../features/toaster';
import { api } from '../../../lib/apiClient';
import { handleTooManyImportsError } from '../../../lib/errorHandlers';
import {
  constructDocumentUrl,
  getIDFromString,
  getIDsFromURLs,
} from '../../../lib/url';
import { buildReExtractPayload } from '../../../redux/modules/annotation/helpers';
import { throwInfo } from '../../../redux/modules/messages/actions';
import { startValidation } from '../../../redux/modules/ui/actions';
import { metrics } from '../../../timers/metrics/metrics';
import { Url } from '../../../types/basic';
import { useCreateRelationDialog } from '../../custom-relations/create-dialog/useCreateRelationDialog';
import { useAddAttachmentsModal } from '../../document/annotation-attachment/hooks/useAddAttachmentsModal';
import { useGuardedDownloadOriginalDocuments } from '../../document/hooks/useGuardedDownloadOriginalDocuments';
import { OnLabelAction } from '../../labels/AnnotationsLabelsList';
import { useRequestLabelsApply } from '../../labels/hooks/useRequestLabelsApply';
import { SelectionPanelButtonAction } from '../annotation-actions/config';
import { DownloadFormat } from '../export-menu/formats';
import { useExportAnnotationsCrossQueue } from '../hooks/useExportAnnotationsCrossQueue';
import {
  ALL_DOCUMENTS_QUERY_KEY,
  useInvalidateDashboardData,
  useSetDashboardData,
} from '../hooks/useFetchDashboardData';
import { usePurgeModal } from '../hooks/usePurgeModal';
import { QUERY_KEY_STATUS_COUNTS } from '../statuses/hooks/useStatusTabCounts';
import { StatusTabKeys } from '../statuses/tabs';
import { AllDocsAnnotation } from '../types';
import { handleLabelsUpdate } from './handleLabelsUpdate';
import { useDeleteAnnotations } from './hooks/useDeleteAnnotations';
import { useMoveAnnotationsModal } from './hooks/useMoveAnnotationsModal';
import { useMoveToReviewModal } from './hooks/useMoveToReviewModal';
import { usePatchAnnotations } from './hooks/usePatchAnnotations';
import { usePostponeAnnotations } from './hooks/usePostponeAnnotations';
import { usePurgeAnnotations } from './hooks/usePurgeAnnotations';
import {
  getNextTrainingEnabledValue,
  useSetTrainingEnabled,
} from './hooks/useSetTrainingEnabled';

export type ActionFnParams = {
  annotations: AllDocsAnnotation[];
  onSuccess?: () => void;
};

type ActionFn = (params: ActionFnParams) => void;

type OnBatchActionParams = {
  urls: Url[];
  parentUrl?: Url;
  onSuccess?: () => void;
};

type BatchActionWithModal = Exclude<
  AnnotationsActionModalProps['action'],
  'reject' | undefined
>;

type DownloadAllPayload = {
  format: DownloadFormat;
  selectedAnnotationsIds?: number[];
};

type ExportPayload = {
  selectedAnnotationsIds?: number[];
  // No `format` — export only, no file download.
  format?: DownloadFormat;
};

type DownloadActions = {
  download: (context: DownloadAllPayload) => void;
  asyncDownload: (params: ActionFnParams) => void;
  export: (context: ExportPayload) => void;
};

type LabelAction = {
  label: OnLabelAction;
};

type TrainingToggleAction = {
  toggleTraining: ActionFn;
};

type ActionsWithCommonParams =
  | 'addAttachment'
  | 'createRelation'
  | Exclude<SelectionPanelButtonAction, 'export' | 'label'>;

export type DocListAnnotationActions = DownloadActions &
  LabelAction &
  TrainingToggleAction &
  Record<ActionsWithCommonParams, ActionFn>;

type AnnotationActions = DocListAnnotationActions | null;

type ContextValue = {
  annotationActions: AnnotationActions;
  activeTabKey: StatusTabKeys | null;
  queueExportSettings: Record<string, boolean>;
};

type Props = {
  children: ReactNode;
  activeQueueName: string | null;
  activeTabKey: StatusTabKeys | null;
  queues?: Queue[];
};

const Context = createContext<ContextValue>({
  annotationActions: null,
  activeTabKey: null,
  queueExportSettings: {},
});

export const useAnnotationActionsContext = () => {
  const ctx = useContext(Context);

  if (ctx === undefined) {
    throw new Error(
      '`useAnnotationActionsContext` must be used within a AnnotationActionsContext provider'
    );
  }

  return ctx;
};

const AnnotationActionsContext = ({
  children,
  activeQueueName,
  activeTabKey,
  queues = [],
}: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const setDashboardData = useSetDashboardData();

  const onBatchValidation = ({ urls }: { urls: Url[] }) => {
    const annotationId = urls.length ? getIDFromString(urls[0]) : undefined;

    if (annotationId) {
      metrics.startAnnotationOpen(annotationId);
    }

    return (
      annotationId &&
      dispatch(
        startValidation(
          constructDocumentUrl({ id: annotationId }),
          getIDsFromURLs(urls)
        )
      )
    );
  };

  const queryClient = useQueryClient();

  const { mutate: onApplyLabel } = useRequestLabelsApply();

  const onBatchLabel = ({
    payload,
    onSuccess,
    onError,
  }: Parameters<DocListAnnotationActions['label']>[0]) => {
    return onApplyLabel(payload, {
      onSuccess: () => {
        onSuccess?.();

        setDashboardData(response => {
          if (!response) return response;

          return {
            ...response,
            results: handleLabelsUpdate(response.results, payload, {
              // TODO: Add atual labels in query here - we need to figure out how to get current labels from dashboard filter
              labelsInQuery: [],
            }),
          };
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [ALL_DOCUMENTS_QUERY_KEY],
        });
      },
      onError: () => {
        onError?.();
      },
    });
  };

  const batchOnSettled = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_STATUS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_QUEUES_UNPAGINATED],
    });
    return queryClient.invalidateQueries({
      queryKey: [ALL_DOCUMENTS_QUERY_KEY],
    });
  };

  const { node: moveModal, openModal } = useMoveAnnotationsModal();
  const { node: moveToReviewModal, openModal: openMoveToReviewModal } =
    useMoveToReviewModal();

  const { mutate: purgeAnnotations } = usePurgeAnnotations();
  const { node: purgeModal, openPurgeModal } = usePurgeModal();

  const { mutate: download } = useExportAnnotationsCrossQueue();

  const downloadOriginalDocuments = useGuardedDownloadOriginalDocuments();

  const [action, setAction] = useState<{
    name: BatchActionWithModal;
    selectedUrls: Array<Url>;
    parentUrl?: Url;
    onSuccess?: () => void;
  } | null>(null);

  const onClose = () => setAction(null);

  const onBatchAction =
    (name: BatchActionWithModal) =>
    ({ urls, parentUrl, onSuccess }: OnBatchActionParams) => {
      setAction({ name, selectedUrls: urls, parentUrl, onSuccess });
    };

  const { setTrainingEnabled } = useSetTrainingEnabled();
  const { mutate: patchAnnotations } = usePatchAnnotations();

  const invalidateDashboardData = useInvalidateDashboardData();
  const { node: addAttachmentModal, openModal: openAddAttachmentModal } =
    useAddAttachmentsModal({ onClose: invalidateDashboardData });

  const { node: createRelationDialog, openDialog: openCreateRelationDialog } =
    useCreateRelationDialog({ onClose: invalidateDashboardData });

  // this is a util fn to avoid extra code. It converts incoming annotations into urls.
  const retrieveUrl =
    (actionFn: ReturnType<typeof onBatchAction> | typeof onBatchValidation) =>
    ({
      annotations,
      ...rest
    }: Omit<OnBatchActionParams, 'urls'> & ActionFnParams) => {
      const urls = annotations.map(a => a.url);
      return actionFn({ urls, ...rest });
    };

  const actions: DocListAnnotationActions = {
    validate: retrieveUrl(onBatchValidation),
    label: onBatchLabel,
    postpone: retrieveUrl(onBatchAction('annotationSkip')),
    delete: retrieveUrl(onBatchAction('annotationDelete')),
    reExtract: retrieveUrl(onBatchAction('reExtract')),
    reAutomate: retrieveUrl(onBatchAction('reAutomate')),
    reprocess: retrieveUrl(onBatchAction('reprocessToToReview')),
    reprocessConfirmed: retrieveUrl(onBatchAction('reprocessToConfirmed')),
    move: ({ annotations, onSuccess }) => {
      openModal({
        annotations,
        onSuccess,
        onSettled: batchOnSettled,
      });
    },
    moveToReview: ({ annotations, onSuccess }: ActionFnParams) => {
      openMoveToReviewModal({
        onConfirm: () => {
          patchAnnotations(
            annotations.map(({ id }) => ({
              id,
              payload: { status: 'to_review' as const },
            })),
            {
              onSuccess: () => {
                onSuccess?.();
              },
              onSettled: () => batchOnSettled(),
            }
          );
        },
      });
    },
    purge: retrieveUrl(({ urls, onSuccess }: OnBatchActionParams) => {
      openPurgeModal(
        {
          annotationCount: urls.length,
          payload: { annotations: urls },
        },
        {
          onSubmit: ({ payload, annotationCount }) =>
            purgeAnnotations(payload, {
              onSuccess: () => {
                dispatch(
                  throwInfo('annotationPurged', {
                    count: annotationCount,
                  })
                );
                onSuccess?.();
              },
              onSettled: batchOnSettled,
            }),
        }
      );
    }),
    download: ({ format, selectedAnnotationsIds }) => {
      download({
        format,
        selectedAnnotationsIds,
        queueName: activeQueueName,
      });
    },
    asyncDownload: ({ annotations }: ActionFnParams) => {
      downloadOriginalDocuments(
        annotations.map(({ document, attachmentStatus }) => ({
          url: document,
          attachmentStatus,
        }))
      );
    },
    export: ({ selectedAnnotationsIds, format }) => {
      const idsSet = new Set(selectedAnnotationsIds);

      setDashboardData(response => {
        if (!response) return response;

        return {
          ...response,
          results: response.results.map(annotation =>
            idsSet.has(annotation.id)
              ? { ...annotation, status: 'exporting' as const }
              : annotation
          ),
        };
      });

      download(
        {
          format,
          selectedAnnotationsIds,
          toExport: true,
          queueName: activeQueueName,
        },
        { onSettled: batchOnSettled }
      );
    },
    addAttachment: retrieveUrl(({ urls }: { urls: Url[] }) => {
      const annotationUrl = urls[0];
      if (annotationUrl) openAddAttachmentModal(annotationUrl);
    }),
    createRelation: ({ annotations }: ActionFnParams) => {
      const annotation = annotations[0];
      if (annotation)
        openCreateRelationDialog(annotation.url, {
          defaultQueueUrl: annotation.queue,
        });
    },
    toggleTraining: ({ annotations, onSuccess }: ActionFnParams) => {
      setTrainingEnabled({
        annotationIds: annotations.map(a => a.id),
        enabled: getNextTrainingEnabledValue(annotations),
        onSuccess,
        onSettled: batchOnSettled,
      });
    },
  };

  const { mutate: postponeAnnotations } = usePostponeAnnotations();
  const { mutate: deleteAnnotations } = useDeleteAnnotations();

  const batchOnSuccess = (
    throwInfoKey?: 'annotationPostponedNoTab' | 'annotationDeletedNoTab'
  ) => {
    if (throwInfoKey) {
      dispatch(
        throwInfo(throwInfoKey, {
          count: action?.selectedUrls.length ?? 0,
        })
      );
    }
    onClose();
    action?.onSuccess?.();
  };

  const reprocessRequest = (status: 'confirmed' | 'to_review') =>
    patchAnnotations(
      getIDsFromURLs(action?.selectedUrls ?? []).map(id => ({
        id,
        payload: { status },
      })),
      {
        onSuccess: () => batchOnSuccess(),
        onSettled: () => batchOnSettled(),
      }
    );

  const batchRequests: Record<BatchActionWithModal, () => void> = {
    annotationSkip: () => {
      postponeAnnotations(getIDsFromURLs(action?.selectedUrls ?? []), {
        onSuccess: () => batchOnSuccess('annotationPostponedNoTab'),
        onSettled: () => batchOnSettled(),
      });
    },
    annotationDelete: () => {
      deleteAnnotations(getIDsFromURLs(action?.selectedUrls ?? []), {
        onSuccess: () => batchOnSuccess('annotationDeletedNoTab'),
        onSettled: () => batchOnSettled(),
      });
    },
    reExtract: () => {
      patchAnnotations(
        getIDsFromURLs(action?.selectedUrls ?? []).map(id => ({
          id,
          payload: buildReExtractPayload(),
        })),
        {
          onSuccess: () => batchOnSuccess(),
          onError: (error: unknown) => {
            try {
              handleTooManyImportsError(error, intl);
            } catch {
              notify.error({
                title: intl.formatMessage({
                  id: 'components.annotationOverview.actionButtons.reExtract.error',
                }),
              });
            }
          },
          onSettled: () => batchOnSettled(),
        }
      );
    },
    reAutomate: () => {
      api
        .request(
          endpoints.internal.reautomate({
            annotations: action?.selectedUrls ?? [],
          })
        )
        .then(() => batchOnSuccess())
        .catch((error: unknown) => {
          try {
            handleTooManyImportsError(error, intl);
          } catch {
            notify.error({
              title: intl.formatMessage({
                id: 'components.annotationOverview.actionButtons.reAutomate.error',
              }),
            });
          }
        })
        .finally(() => batchOnSettled());
    },
    reprocessToConfirmed: () => reprocessRequest('confirmed'),
    reprocessToToReview: () => reprocessRequest('to_review'),
  };

  const queueExportSettings = useMemo(
    () =>
      Object.fromEntries(
        queues.map(q => [q.url, !q.settings?.hideExportButton])
      ),
    [queues]
  );

  return (
    <Context.Provider
      value={{ annotationActions: actions, activeTabKey, queueExportSettings }}
    >
      {children}

      {moveModal}
      {moveToReviewModal}
      {purgeModal}
      {addAttachmentModal}
      {createRelationDialog}
      <AnnotationsActionModal
        preventOnClose
        dataCyConfirm="modal-confirm-button"
        action={action?.name}
        annotationsLength={action?.selectedUrls.length ?? 0}
        onClose={onClose}
        onPrimaryActionClick={() => {
          if (action?.name) {
            batchRequests[action.name]();
          }
        }}
      />
    </Context.Provider>
  );
};

export { AnnotationActionsContext };
